<template>
    <v-autocomplete
        v-model="item"
        :items="items"
        :loading="isLoading"
        hide-no-data
        hide-selected
        :label="label"
        no-filter
        clearable
        :rules="rules"
        :disabled="disabled"
        :hide-details="hideDetails"
        :single-line="singleLine"
        :density="density"
        :item-title="itemText"
        :item-value="itemValue"
        :return-object="returnObject"
        @update:search="onSearch"
    >
        <template v-slot:item="{item, props}">
            <v-list-item v-bind="props" :prepend-icon="prependIcon">
                <template v-slot:title>
                    {{ getLabel(item.raw) }}
                </template>
            </v-list-item>
        </template>
        <template v-slot:selection="{item}">
            {{ getLabel(item.raw) }}
        </template>
    </v-autocomplete>
</template>
<script setup lang="ts">
    const props = withDefaults(
        defineProps<{
            disabled?: boolean
            singleLine?: boolean
            hideDetails?: boolean | 'auto'
            rules?: Array<((value: any) => true | string) | string>
            density?: 'comfortable' | 'compact'
            label: string
            appMethod: string
            searchType: string
            itemValue?: string
            itemText?: string
            defaultRequest?: any
            searchKey?: string
            limit?: number
            returnObject?: boolean
            initialSearchValue?: any
        }>(),
        {
            rules: () => [],
            density: 'comfortable',
            hideDetails: 'auto',
            itemValue: 'id',
            itemText: 'name',
            defaultRequest: {},
            searchKey: 'pattern',
            limit: 10,
            returnObject: false,
            initialSearchValue: null,
        }
    )

    const item = defineModel<string | number>()

    const isLoading = ref<boolean>(false)
    const items = ref<any[]>([])
    const timer = ref<any>()

    const {$api} = useNuxtApp()

    onMounted(async () => {
        if (item.value) {
            let searchParams = {[props.itemValue]: item.value}
            if (props.initialSearchValue && props.searchType === 'HOTEL_NAME') {
                searchParams = props.initialSearchValue
            }
            await initSearch(searchParams)
        }
    })

    const onSearch = val => {
        if (!val) return

        if (timer.value) clearTimeout(timer.value)
        const searchValue = props.appMethod === 'locations' ? `%${val}%` : val
        timer.value = window.setTimeout(() => {
            initSearch({[props.searchKey]: searchValue})
        }, 400)
    }

    const initSearch = async rq => {
        const request = {...props.defaultRequest, ...rq}

        isLoading.value = true
        items.value = []
        try {
            const resp = await $api[props.appMethod].get(request)
            items.value = await responseAdapter(resp)
        } finally {
            isLoading.value = false
        }
    }

    const responseAdapter = async resp => {
        switch (props.searchType) {
            case 'HOTEL_NAME':
                return resp.hotels.map(({id, location, name}) => ({id, location, name}))
            case 'SUPPLIER':
                return resp.suppliers
            case 'CITY':
                return resp.cities
            case 'COUNTRY':
                return resp.countries
            case 'COMPOSITE_LOCATION':
                return [
                    ...resp.cities,
                    ...resp.countries.map(item => {
                        return {...item, countryId: item.id}
                    }),
                ]
            default:
                return resp
        }
    }

    const getLabel = entry => {
        if (!entry) return

        if (items.value?.length && typeof entry !== 'object') {
            const obj = items.value.find(item => item[props.itemValue] == entry)
            if (obj) entry = obj
            else return
        }
        if (entry !== null && typeof entry === 'object') {
            let elements
            switch (props.appMethod) {
                case 'locations':
                    elements = [
                        entry?.name,
                        entry?.parentName,
                        entry?.parentName !== entry?.countryName ? entry?.countryName : '',
                    ]
                    return elements.filter(element => element).join(', ')
                case 'hotels':
                case 'suggestHotels':
                    elements = [entry?.name, entry?.location?.cityName, entry?.location?.countryName]
                    return elements.filter(element => element).join(', ')
                default:
                    return `${entry[props.itemText]}`
            }
        }
    }

    const prependIcon = computed(() => {
        switch (props.appMethod) {
            case 'suppliers':
                return 'mdi-account-multiple'
            case 'locations':
                return 'mdi-earth'
            case 'hotels':
            case 'suggestHotels':
                return 'mdi-office-building-outline'
            default:
                return ''
        }
    })
</script>
